import React, { useEffect, useState } from "react";

import Paper from "@material-ui/core/Paper";
import Skeleton from "@material-ui/lab/Skeleton";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { Box, Typography, IconButton, Dialog, DialogTitle, DialogContent } from "@material-ui/core";
import StarIcon from "@material-ui/icons/Star";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { Bar } from "react-chartjs-2";
import "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import api from "../../services/api";

const useStyles = makeStyles((theme) => ({
  container: {
    borderRadius: 14,
    border: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9"}`,
    boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
    overflow: "hidden",
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    padding: "14px 18px",
    borderBottom: `1px solid ${theme.palette.type === "dark" ? "rgba(255,255,255,0.06)" : "#F1F5F9"}`,
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  headerIcon: {
    color: "#F59E0B",
    fontSize: 22,
  },
  headerTitle: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    fontWeight: 700,
    color: theme.palette.type === "dark" ? "#F1F5F9" : "#1E293B",
  },
  infoIconBtn: {
    padding: 4,
    color: theme.palette.type === "dark" ? "rgba(255,255,255,0.35)" : "rgba(0,0,0,0.35)",
    "&:hover": {
      color: theme.palette.type === "dark" ? "rgba(255,255,255,0.6)" : "rgba(0,0,0,0.55)",
      backgroundColor: "transparent",
    },
  },
  dialogContent: {
    "& p": { marginBottom: theme.spacing(1.5) },
    "& p:last-child": { marginBottom: 0 },
  },
  ruleItem: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: theme.spacing(1),
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
  },
  ruleBullet: {
    width: 10,
    height: 10,
    borderRadius: "50%",
  },
  body: {
    display: "flex",
    flexDirection: "column",
    padding: "16px 18px",
    flex: 1,
    minHeight: 0,
  },
  chartContainer: {
    width: "100%",
    height: 160,
    position: "relative",
  },
  emptyMessage: {
    fontFamily: '"Inter", "Poppins", sans-serif',
    fontSize: 14,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#94A3B8" : "#64748B",
    textAlign: "center",
    padding: theme.spacing(2),
  },
  skeleton: {
    borderRadius: 14,
  },
}));

export default function TableMediaStatus(props) {
  const { loading, attendants, dataini, dataFinal } = props;
  const classes = useStyles();
  const theme = useTheme();
  const [dougsData, setDougsData] = useState(null);
  const [infoModalOpen, setInfoModalOpen] = useState(false);

  useEffect(() => {
    getNpmInformations();
  }, [dataini, dataFinal]);

  async function getNpmInformations() {
    try {
      const params = {
        initialDate: dataini,
        finalDate: dataFinal,
      };
      const { data } = await api.get("/dashboard/nps-info", { params });

      if (data.length > 0 && data[0].total_notas > 0) {
        const percentagens = {
          boa: (data[0].boa / data[0].total_notas) * 100,
          media: (data[0].media / data[0].total_notas) * 100,
          ruim: (data[0].ruim / data[0].total_notas) * 100,
        };
        setDougsData(percentagens);
      } else {
        setDougsData({ boa: 0, media: 0, ruim: 0 });
      }
    } catch (error) {
      console.log("erro ao buscar informações", error);
    }
  }

  const labels = ["Ótimo", "Bom", "Ruim"];
  const values = dougsData
    ? [dougsData.boa, dougsData.media, dougsData.ruim]
    : [0, 0, 0];
  const colors = ["#35af9a", "#F59E0B", "#EF4444"];

  const chartData = {
    labels,
    datasets: [
      {
        label: "%",
        data: values,
        backgroundColor: colors,
        hoverBackgroundColor: ["#2d9a87", "#D97706", "#DC2626"],
        borderWidth: 0,
        borderRadius: 6,
        barThickness: 28,
      },
    ],
  };

  const chartOptions = {
    indexAxis: "y",
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { left: 4, right: 40 },
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        grid: { display: false },
        ticks: {
          callback: (v) => v + "%",
          font: { size: 11, family: "Inter" },
          color: theme.palette?.type === "dark" ? "#94A3B8" : "#64748B",
        },
      },
      y: {
        grid: { display: false },
        ticks: {
          font: { size: 12, weight: 600, family: "Inter" },
          color: theme.palette?.type === "dark" ? "#F1F5F9" : "#1E293B",
        },
      },
    },
    plugins: {
      legend: { display: false },
      datalabels: {
        anchor: "end",
        align: "end",
        offset: 4,
        color: (ctx) => (ctx.raw > 15 ? "#FFFFFF" : "#1E293B"),
        font: { size: 11, weight: "bold", family: "Inter" },
        formatter: (value) => (value > 0 ? `${value.toFixed(0)}%` : ""),
      },
    },
  };

  return !loading ? (
    <Paper elevation={0} className={classes.container}>
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <StarIcon className={classes.headerIcon} />
          <Typography className={classes.headerTitle}>
            Avaliação dos Atendimentos
          </Typography>
        </Box>
        <IconButton
          size="small"
          className={classes.infoIconBtn}
          onClick={() => setInfoModalOpen(true)}
          aria-label="Como funciona a avaliação"
        >
          <InfoOutlinedIcon style={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      <Dialog
        open={infoModalOpen}
        onClose={() => setInfoModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          style: {
            borderRadius: 14,
            padding: 0,
          },
        }}
      >
        <DialogTitle style={{ fontFamily: '"Inter", "Poppins", sans-serif', fontSize: 16, fontWeight: 700 }}>
          Como funciona a avaliação
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography variant="body2" color="textSecondary" style={{ marginBottom: 12 }}>
            As notas são de 0 a 10 e são agrupadas da seguinte forma:
          </Typography>
          <Box className={classes.ruleItem}>
            <Box className={classes.ruleBullet} style={{ backgroundColor: "#EF4444" }} />
            <Typography variant="body2"><strong>Ruim:</strong> notas de 0 a 3</Typography>
          </Box>
          <Box className={classes.ruleItem}>
            <Box className={classes.ruleBullet} style={{ backgroundColor: "#F59E0B" }} />
            <Typography variant="body2"><strong>Bom:</strong> notas de 4 a 6</Typography>
          </Box>
          <Box className={classes.ruleItem}>
            <Box className={classes.ruleBullet} style={{ backgroundColor: "#35af9a" }} />
            <Typography variant="body2"><strong>Ótimo:</strong> notas de 7 a 10</Typography>
          </Box>
        </DialogContent>
      </Dialog>
      <Box className={classes.body}>
        {dougsData ? (
          dougsData.boa === 0 && dougsData.media === 0 && dougsData.ruim === 0 ? (
            <Typography className={classes.emptyMessage}>
              Nenhuma avaliação foi feita ainda
            </Typography>
          ) : (
            <Box className={classes.chartContainer}>
              <Bar
                data={chartData}
                options={chartOptions}
                plugins={[ChartDataLabels]}
              />
            </Box>
          )
        ) : (
          <Skeleton variant="rect" height={150} />
        )}
      </Box>
    </Paper>
  ) : (
    <Skeleton variant="rect" height={200} className={classes.skeleton} />
  );
}
